/**
 * ReservePreliminaryWorkGate — resolves whether the "Zarezerwuj wstępnie"
 * action has anything to do for a version (an accepted nośnik with an
 * accepted period to reserve, or a rejected nośnik still holding an active
 * reservation to free), so the finalization panel can disable the action
 * with an explanation when neither half has work (offer-accept-reservation
 * / "Nothing to reserve and nothing to release disables the action").
 *
 * Its own component so the unit-decisions request is only issued when the
 * caller may actually reserve.
 */
import type { ReactNode } from 'react';
import { useUnitDecisions } from '../versions/api/previews';
import { isLockable } from '../versions/unit-decisions/filters';
import type { UnitDecisionsListItem } from '@ooh/contracts';

function hasReleasableRejection(item: UnitDecisionsListItem): boolean {
  return (
    item.decision === 'REJECTED' &&
    (item.reservationState === 'SOFT_RESERVATION' || item.reservationState === 'OPTION')
  );
}

export function ReservePreliminaryWorkGate({
  offerId,
  versionId,
  children,
}: {
  offerId: string;
  versionId: number;
  children: (hasWork: boolean) => ReactNode;
}) {
  const query = useUnitDecisions(offerId, versionId);
  const items = query.data ?? [];
  // Default to "there might be work" while loading/erroring rather than
  // prematurely disabling the action — the dialog's own two breakdowns are
  // the authoritative source of truth once opened.
  const hasWork =
    query.isLoading ||
    query.isError ||
    items.some(isLockable) ||
    items.some(hasReleasableRejection);
  return <>{children(hasWork)}</>;
}
